"use client"

import { Controller, FieldPath, FieldValues, useFormContext } from "react-hook-form"
import { TIconButton } from "../../ui/button/TIconButton"
import { TText } from "../../ui/display/TText"
import { TNumberInput } from "../../ui/input/TNumberInput"
import { TSlider } from "../../ui/slider/TSlider"
import { FormControl, FormItem, FormMessage } from "../../ui/shadcn/form"
import { DynamicFormLabel } from "../components/form-label"
import type { ParsedField } from "../types"

interface IntegerFieldProps {
	field: ParsedField
}

export function IntegerField({ field: parsedField }: IntegerFieldProps) {
	const { name, schema, required } = parsedField
	const description = schema.description
	const title = schema.title
	const { control } = useFormContext()
	const min = (schema.minimum as number) || 0
	const max = (schema.maximum as number) || 100
	const step = 1
	const counter = false
	const defaultValue = schema.default !== undefined ? (schema.default as number) : min

	return (
		<Controller
			name={name as FieldPath<FieldValues>}
			control={control}
			render={({ field }) => {
				if (counter) {
					const currentValue = field.value ?? defaultValue

					const handleSubtract = () => {
						const newValue = Math.max(min, currentValue - step)
						field.onChange(newValue)
					}

					const handleAdd = () => {
						const newValue = Math.min(max, currentValue + step)
						field.onChange(newValue)
					}

					const isSubtractDisabled = currentValue <= min
					const isAddDisabled = currentValue >= max

					return (
						<div className="h-10 bg-g800/10 rounded-lg px-3 flex items-center justify-between group">
							<DynamicFormLabel name={name} title={title} required={required} description={description} />
							<div className="flex items-center space-x-1">
								<TIconButton name="subtract" size="xsmall" color="line" onClick={handleSubtract} disabled={isSubtractDisabled} />
								<TText color="text" className="w-7 text-center">
									{currentValue}
								</TText>
								<TIconButton name="add" size="xsmall" color="line" onClick={handleAdd} disabled={isAddDisabled} />
							</div>
						</div>
					)
				}

				return (
					<FormItem className="bg-g800/10 rounded-lg p-3 flex flex-col group">
						<DynamicFormLabel name={name} title={title} required={required} description={description} />
						<div className="flex items-center gap-3">
							<FormControl>
								<TSlider
									name={name}
									value={field.value ? [field.value] : [defaultValue]}
									onChange={(value) => field.onChange(value[0])}
									min={min}
									max={max}
									step={step}
								/>
							</FormControl>
							<FormControl>
								<TNumberInput
									name={name}
									value={field.value ?? defaultValue}
									onChange={(value) => field.onChange(value)}
									min={min}
									max={max}
									step={step}
									integerOnly
									size="small"
									className="w-20! shrink-0 [&_input]:min-w-0!"
								/>
							</FormControl>
						</div>
						<FormMessage />
					</FormItem>
				)
			}}
		/>
	)
}
